import { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// Styled Components (matching Global Title Builder design)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const TitleInputContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 80px;
  border: 2px solid ${(props) => (props.isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  background: white;
  padding: 0.75rem;
  transition: all 0.2s ease;
  cursor: text;
`;

const TitleInput = styled.div`
  width: 100%;
  min-height: auto;
  font-size: 1rem;
  color: #1f2937;
  line-height: 2;
  outline: none;
  border: none;
  background: transparent;
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 0.25rem 0;

  &[contenteditable="true"]:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
    pointer-events: none;
  }

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.125rem;
    user-select: none;
    cursor: default;
    contenteditable: false;

    .field-badge-remove {
      background: transparent;
      border: none;
      color: #ef4444;
      cursor: pointer;
      padding: 0;
      font-size: 0.875rem;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s ease;
      margin-left: 0.125rem;

      &:hover {
        color: #dc2626;
      }
    }
  }
`;

const ColumnTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.625rem;
  border-bottom: 2px solid #e5e7eb;
  letter-spacing: -0.01em;
`;

const AvailableFieldsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FieldItem = styled.div`
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  cursor: move;
  display: flex;
  align-items: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  user-select: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;

  &:hover {
    background: #e5e7eb;
    border-color: rgba(0, 0, 0, 0.4);
  }

  &.dragging {
    opacity: 0.5;
  }
`;

const PreviewBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  min-height: 80px;
  color: #111827;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: not-allowed;

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.125rem;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const Button = styled.button`
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 120px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.primary {
    background: #3b82f6;
    color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover:not(:disabled) {
      background: #2563eb;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
  }

  &.secondary {
    background: #f8fafc;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      background: #f1f5f9;
      border-color: #9ca3af;
    }
  }

  &.small {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    min-width: 80px;
  }
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

const SuccessMessage = styled.p`
  color: #059669;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  padding: 0.75rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
`;

const TypeSelectionContainer = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

function TitleBuilder({
  isOpen,
  onClose,
  availableFields,
  titleBuilderType,
  onTypeChange,
  currentBatchId,
  apiBaseUrl,
  refreshAccessToken,
  onSave,
}) {
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isDraggingOverInput, setIsDraggingOverInput] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalReady, setIsModalReady] = useState(false);
  const titleInputRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const lastConfigRef = useRef(null);

  // Example values for preview
  const getExampleValue = (fieldName) => {
    const examples = {
      "Card Name": "Pikachu",
      Type: "Electric",
      Manufacturer: "Pokemon",
      Set: "Base Set",
      "Card Number": "25",
      "Year Manufactured": "1999",
    };
    return examples[fieldName] || fieldName;
  };

  // Rebuild contentEditable from configuration array
  const rebuildFromConfiguration = (config) => {
    if (!titleInputRef.current || !Array.isArray(config)) return;

    // Clear existing content
    titleInputRef.current.innerHTML = "";

    // Rebuild content from configuration
    config.forEach((item) => {
      if (item.type === "string") {
        const textNode = document.createTextNode(item.value || "");
        titleInputRef.current.appendChild(textNode);
      } else if (item.type === "field-name") {
        const fieldBadge = document.createElement("span");
        fieldBadge.className = "field-badge";
        fieldBadge.setAttribute("contenteditable", "false");
        fieldBadge.setAttribute("data-field", item.value || "");

        const fieldNameSpan = document.createElement("span");
        fieldNameSpan.textContent = item.value || "";
        fieldBadge.appendChild(fieldNameSpan);

        const removeButton = document.createElement("button");
        removeButton.className = "field-badge-remove";
        removeButton.textContent = "×";
        removeButton.setAttribute("contenteditable", "false");
        removeButton.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          removeFieldBadge(fieldBadge);
        };
        fieldBadge.appendChild(removeButton);

        titleInputRef.current.appendChild(fieldBadge);
        
        // Add a space after the field badge
        const spaceNode = document.createTextNode(" ");
        titleInputRef.current.appendChild(spaceNode);
      }
    });

    updatePreview();
  };

  // Remove field badge
  const removeFieldBadge = (badgeElement) => {
    if (!badgeElement || !titleInputRef.current) return;

    const textNode = document.createTextNode("");
    badgeElement.parentNode.replaceChild(textNode, badgeElement);

    const selection = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updatePreview();
  };

  // Insert field at cursor position
  const insertFieldAtCursor = (fieldName) => {
    if (!titleInputRef.current) return;

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(titleInputRef.current);
      range.collapse(false);
    }

    const fieldBadge = document.createElement("span");
    fieldBadge.className = "field-badge";
    fieldBadge.setAttribute("contenteditable", "false");
    fieldBadge.setAttribute("data-field", fieldName);

    const fieldNameSpan = document.createElement("span");
    fieldNameSpan.textContent = fieldName;
    fieldBadge.appendChild(fieldNameSpan);

    const removeButton = document.createElement("button");
    removeButton.className = "field-badge-remove";
    removeButton.textContent = "×";
    removeButton.setAttribute("contenteditable", "false");
    removeButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      removeFieldBadge(fieldBadge);
    };
    fieldBadge.appendChild(removeButton);

    range.deleteContents();
    range.insertNode(fieldBadge);

    // Add a space after the field badge
    const spaceNode = document.createTextNode(" ");
    range.setStartAfter(fieldBadge);
    range.insertNode(spaceNode);

    range.setStartAfter(spaceNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updatePreview();
  };

  // Generate preview HTML
  const generatePreview = () => {
    if (!titleInputRef.current) {
      return "Your title preview will appear here...";
    }

    const config = formatConfiguration();
    if (config.length === 0) {
      return "Your title preview will appear here...";
    }

    let previewText = "";
    config.forEach((item) => {
      if (item.type === "string") {
        previewText += item.value;
      } else if (item.type === "field-name") {
        const exampleValue = getExampleValue(item.value);
        previewText += exampleValue;
      }
    });

    const escapedText = previewText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    return escapedText || "Your title preview will appear here...";
  };

  // Update preview
  const updatePreview = () => {
    if (!titleInputRef.current) {
      setPreview("");
      return;
    }

    setPreview(generatePreview());
  };

  // Format configuration for API
  const formatConfiguration = () => {
    if (!titleInputRef.current) return [];

    const config = [];

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          config.push({
            type: "string",
            value: text,
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList && node.classList.contains("field-badge")) {
          const fieldName = node.getAttribute("data-field");
          if (fieldName) {
            config.push({
              type: "field-name",
              value: fieldName,
            });
          }
        } else {
          for (let child = node.firstChild; child; child = child.nextSibling) {
            processNode(child);
          }
        }
      }
    };

    for (
      let child = titleInputRef.current.firstChild;
      child;
      child = child.nextSibling
    ) {
      processNode(child);
    }

    return config;
  };

  // Reset modal readiness when it closes
  useEffect(() => {
    if (!isOpen) {
      setIsModalReady(false);
      // Reset when modal closes
      hasLoadedRef.current = false;
      lastConfigRef.current = null;
    }
  }, [isOpen]);

  // Load configuration when modal opens or type changes
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Create a unique key for this configuration request
    const configKey = `${titleBuilderType}-${currentBatchId || "global"}`;

    // If type or batch changed, reset loaded flag
    if (lastConfigRef.current && lastConfigRef.current !== configKey) {
      hasLoadedRef.current = false;
    }

    // Skip if we've already loaded this exact configuration
    if (hasLoadedRef.current && lastConfigRef.current === configKey) {
      return;
    }

    const loadConfiguration = async () => {
      setError("");
      setSuccess("");
      setIsLoading(true);
      setIsModalReady(false);

      try {
        let token = localStorage.getItem("access");
        if (!token && refreshAccessToken) token = await refreshAccessToken();

        // If global type, hit global endpoint; otherwise hit batch-specific API
        // For batch type, require currentBatchId
        if (titleBuilderType === "batch" && !currentBatchId) {
          setIsLoading(false);
          if (titleInputRef.current) {
            titleInputRef.current.innerHTML = "";
          }
          setPreview("");
          return;
        }

        const url =
          titleBuilderType === "global"
            ? `${apiBaseUrl}/api/v1/cards/title-builder/`
            : `${apiBaseUrl}/api/v1/cards/batches/${currentBatchId}/title-builder/`;

        let response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401 && refreshAccessToken) {
          const newToken = await refreshAccessToken();
          response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (response.ok) {
          const data = await response.json();
          if (data.configuration && Array.isArray(data.configuration)) {
            // Mark as loaded before rebuilding to prevent re-triggering
            hasLoadedRef.current = true;
            lastConfigRef.current = configKey;

            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
              setTimeout(() => {
                if (titleInputRef.current) {
                  rebuildFromConfiguration(data.configuration);
                }
                // Mark as ready after a small delay to ensure all handlers are attached
                setTimeout(() => {
                  setIsModalReady(true);
                }, 50);
              }, 100);
            });
          } else {
            hasLoadedRef.current = true;
            lastConfigRef.current = configKey;
            requestAnimationFrame(() => {
              setTimeout(() => {
                if (titleInputRef.current) {
                  titleInputRef.current.innerHTML = "";
                }
                setPreview("");
                // Mark as ready even with empty configuration
                setTimeout(() => {
                  setIsModalReady(true);
                }, 50);
              }, 100);
            });
          }
        } else if (response.status === 404) {
          // No configuration saved yet
          hasLoadedRef.current = true;
          lastConfigRef.current = configKey;
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (titleInputRef.current) {
                titleInputRef.current.innerHTML = "";
              }
              setPreview("");
              // Mark as ready even with no configuration
              setTimeout(() => {
                setIsModalReady(true);
              }, 50);
            }, 100);
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(
            errorData.detail ||
              errorData.message ||
              `Failed to load configuration: ${response.statusText}`
          );
        }
      } catch (err) {
        console.error("Error fetching title builder configuration:", err);
        setError("Failed to load configuration. Please try again.");
        // Mark as ready even on error
        requestAnimationFrame(() => {
          setTimeout(() => {
            setIsModalReady(true);
          }, 50);
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, titleBuilderType, currentBatchId]);

  // Auto-focus input field when modal is ready
  useEffect(() => {
    if (isOpen && isModalReady && titleInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (titleInputRef.current) {
            titleInputRef.current.focus();
            // Place cursor at the end of content if there's any
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(titleInputRef.current);
            range.collapse(false); // Collapse to end
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 100);
      });
    }
  }, [isOpen, isModalReady]);

  // Handle input
  const handleInput = () => {
    updatePreview();
  };

  // Handle drag over
  const handleDragOver = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingOverInput(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    setIsDraggingOverInput(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverInput(false);

    const field = e.dataTransfer.getData("text/plain");
    if (field) {
      insertFieldAtCursor(field);
    }
  };

  // Handle container drag over (prevent drop on container)
  const handleContainerDragOver = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    // Check if the drop target is the TitleInput or its children
    const isOverInput =
      titleInputRef.current &&
      (titleInputRef.current === e.target ||
        titleInputRef.current.contains(e.target));

    // Only allow drop if over the input, otherwise prevent
    if (!isOverInput) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
    }
  };

  const handleContainerDrop = (e) => {
    if (!isModalReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Check if the drop target is the TitleInput or its children
    const isOverInput =
      titleInputRef.current &&
      (titleInputRef.current === e.target ||
        titleInputRef.current.contains(e.target));

    // Only allow drop if over the input, otherwise prevent
    if (!isOverInput) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      updatePreview();
    }
  };

  // Clear all
  const handleClear = () => {
    if (titleInputRef.current) {
      titleInputRef.current.innerHTML = "";
      setPreview("");
      setError("");
      setSuccess("");
    }
  };

  // Handle save
  const handleSave = async () => {
    const config = formatConfiguration();

    if (config.length === 0) {
      setError("Please add at least one field to save the configuration.");
      setSuccess("");
      return;
    }

    // For batch type, require currentBatchId
    if (titleBuilderType === "batch" && !currentBatchId) {
      setError("No batch selected. Cannot save batch-specific configuration.");
      setSuccess("");
      return;
    }

    // For global type, require currentBatchId to also save to batch endpoint
    if (titleBuilderType === "global" && !currentBatchId) {
      setError("No batch selected. Cannot save configuration to batch.");
      setSuccess("");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccess("");

    try {
      let token = localStorage.getItem("access");
      if (!token && refreshAccessToken) token = await refreshAccessToken();

      const payload = {
        configuration: config,
      };

      // Helper function to make API call with token refresh
      const makeApiCall = async (url, tokenToUse) => {
        let response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 401 && refreshAccessToken) {
          const newToken = await refreshAccessToken();
          response = await fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
        }
        return response;
      };

      if (titleBuilderType === "global") {
        // Hit both global and batch endpoints with same configuration
        const globalUrl = `${apiBaseUrl}/api/v1/cards/title-builder/`;
        const batchUrl = `${apiBaseUrl}/api/v1/cards/batches/${currentBatchId}/title-builder/`;

        const [globalResponse, batchResponse] = await Promise.all([
          makeApiCall(globalUrl, token),
          makeApiCall(batchUrl, token),
        ]);

        if (globalResponse.ok && batchResponse.ok) {
          setSuccess("Title builder settings saved successfully!");
          setError("");

          // Call parent's onSave callback if provided
          if (onSave) {
            onSave(config);
          }

          // Close modal after saving
          setTimeout(() => {
            handleClose();
          }, 1000);
        } else {
          const globalError = globalResponse.ok
            ? null
            : await globalResponse.json().catch(() => ({}));
          const batchError = batchResponse.ok
            ? null
            : await batchResponse.json().catch(() => ({}));

          const errorMessage =
            globalError?.detail ||
            globalError?.message ||
            batchError?.detail ||
            batchError?.message ||
            `Failed to save settings: ${
              !globalResponse.ok ? "Global API failed" : ""
            } ${!batchResponse.ok ? "Batch API failed" : ""}`.trim();
          setError(errorMessage);
          setSuccess("");
        }
      } else {
        // Batch type - only hit batch endpoint
        const batchUrl = `${apiBaseUrl}/api/v1/cards/batches/${currentBatchId}/title-builder/`;
        const response = await makeApiCall(batchUrl, token);

        if (response.ok) {
          setSuccess("Title builder settings saved successfully!");
          setError("");

          // Call parent's onSave callback if provided
          if (onSave) {
            onSave(config);
          }

          // Close modal after saving
          setTimeout(() => {
            handleClose();
          }, 1000);
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.detail ||
            errorData.message ||
            `Failed to save settings: ${response.statusText}`;
          setError(errorMessage);
          setSuccess("");
        }
      }
    } catch (err) {
      const errorMessage =
        err.message || "Failed to save settings. Please try again.";
      setError(errorMessage);
      setSuccess("");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setError("");
    setSuccess("");
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClick={handleClose}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "none";
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        onDragOver={(e) => {
          // Check if the drop target is the TitleInput or its children
          const isOverInput =
            titleInputRef.current &&
            (titleInputRef.current === e.target ||
              titleInputRef.current.contains(e.target));

          // Only allow drop if over the input, otherwise prevent
          if (!isOverInput) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "none";
          }
        }}
        onDrop={(e) => {
          // Check if the drop target is the TitleInput or its children
          const isOverInput =
            titleInputRef.current &&
            (titleInputRef.current === e.target ||
              titleInputRef.current.contains(e.target));

          // Only allow drop if over the input, otherwise prevent
          if (!isOverInput) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <ModalHeader>
          <ModalTitle>Title Builder</ModalTitle>
        </ModalHeader>
        <ModalContent
          onDragOver={(e) => {
            // Prevent drops on modal content area
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "none";
          }}
          onDrop={(e) => {
            // Prevent drops on modal content area
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {/* Type Selection */}
          <TypeSelectionContainer>
            <Label
              style={{
                marginBottom: "0.75rem",
                display: "block",
                fontWeight: 600,
              }}
            >
              Configuration Type:
            </Label>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  value="global"
                  checked={titleBuilderType === "global"}
                  onChange={(e) => onTypeChange && onTypeChange(e.target.value)}
                />
                <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                  Global Title Builder
                </span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: currentBatchId ? "pointer" : "not-allowed",
                }}
              >
                <input
                  type="radio"
                  value="batch"
                  checked={titleBuilderType === "batch"}
                  onChange={(e) => onTypeChange && onTypeChange(e.target.value)}
                  disabled={!currentBatchId}
                />
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: currentBatchId ? "#374151" : "#9ca3af",
                  }}
                >
                  Batch Specific Title Builder
                  {!currentBatchId && " (No batch selected)"}
                </span>
              </label>
            </div>
          </TypeSelectionContainer>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              Loading configuration...
            </div>
          ) : (
            <>
              <div>
                <Label
                  style={{
                    marginBottom: "0.75rem",
                    display: "block",
                    fontWeight: 600,
                  }}
                >
                  Title Input
                </Label>
                <TitleInputContainer
                  isDragging={isDraggingOverInput && isModalReady}
                  onDragOver={handleContainerDragOver}
                  onDrop={handleContainerDrop}
                  style={{
                    opacity: isModalReady ? 1 : 0.7,
                    pointerEvents: isModalReady ? "auto" : "none",
                  }}
                >
                  <TitleInput
                    ref={titleInputRef}
                    contentEditable={isModalReady}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    data-placeholder="Type your title here or drag fields from below..."
                    suppressContentEditableWarning
                    style={{
                      pointerEvents: isModalReady ? "auto" : "none",
                    }}
                  />
                </TitleInputContainer>
              </div>

              <div
                onDragOver={(e) => {
                  // Prevent drops on available fields section
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = "none";
                }}
                onDrop={(e) => {
                  // Prevent drops on available fields section
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ColumnTitle>Available Fields</ColumnTitle>
                <AvailableFieldsList>
                  {availableFields.map((field) => (
                    <FieldItem
                      key={field}
                      draggable={isModalReady}
                      onDragStart={(e) => {
                        if (!isModalReady) {
                          e.preventDefault();
                          return;
                        }
                        setDraggedField(field);
                        e.dataTransfer.setData("text/plain", field);
                        e.dataTransfer.effectAllowed = "copy";
                        if (e.target) {
                          e.target.style.opacity = "0.5";
                        }
                      }}
                      onDragEnd={(e) => {
                        setDraggedField(null);
                        if (e.target) {
                          e.target.style.opacity = "1";
                        }
                      }}
                      className={draggedField === field ? "dragging" : ""}
                      style={{
                        opacity: isModalReady ? 1 : 0.5,
                        cursor: isModalReady ? "move" : "not-allowed",
                      }}
                    >
                      {field}
                    </FieldItem>
                  ))}
                </AvailableFieldsList>
              </div>

              <div
                onDragOver={(e) => {
                  // Prevent drops on preview section
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = "none";
                }}
                onDrop={(e) => {
                  // Prevent drops on preview section
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ColumnTitle>Preview</ColumnTitle>
                <PreviewBox
                  dangerouslySetInnerHTML={{
                    __html: preview || "Your title preview will appear here...",
                  }}
                />
              </div>

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}
            </>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={handleClear}
            className="secondary small"
            disabled={isSaving || isLoading}
          >
            Clear All
          </Button>
          <Button
            onClick={handleSave}
            className="primary small"
            disabled={
              isSaving || isLoading || formatConfiguration().length === 0
            }
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
          <Button
            onClick={handleClose}
            className="secondary small"
            disabled={isSaving || isLoading}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}

export default TitleBuilder;
